// Notifications and reminders (Features 1, 20 Oct): one place every part of the board feeds. Reminders the user or an
// agent sets, and what comes due on the board itself: a post due to go out, a client's
// follow-up, a countdown reaching zero, an event on a Calendar board ten minutes before it starts. Worked out here from the board's own data, in the user's own time zone, so the
// board's alarm raises each once at its time, with the board open or closed. Events (a number crossing a line, an
// agent that needs the user, a plan's question) are raised where they happen.

import type { ActivityTo, Entry, ID, Item } from "./types";
import { addDays, cleanZone, wallClock, weekdayOf } from "./days";
import { dayPart, eventOf, eventTitle, minPart, momentOf, showingsOf } from "./agenda";
import type { GcalEvent } from "./gcal";
import { joinIn } from "./meet";

export type NoticeKind = "reminder" | "post" | "followup" | "countdown" | "threshold" | "agent" | "question" | "event";

/** What the bell lists, and what pops up. */
export interface Notice {
  id: ID;
  /** when it was raised */
  t: number;
  kind: NoticeKind;
  title: string;
  body: string | null;
  /** what a click opens (goto.ts) */
  to: ActivityTo | null;
  /** the board it counts on, in the boards list; null for none */
  board: ID | null;
  /** the same thing is raised once: a reminder at its time, a task on its day */
  key: string;
  read: boolean;
  /** a reminder or a task it can tick when done */
  done?: { reminder?: ID } | null;
  /** an event's call (Calendar step 11): the bell shows Join */
  join?: string;
}

export type Repeat = "daily" | "weekdays" | "weekly" | "monthly";

/** A reminder: words, a time, how it repeats, and what it is about. */
export interface Reminder {
  id: ID;
  text: string;
  at: number;
  repeat: Repeat | null;
  to: ActivityTo | null;
  /** who set it: null for the user, an agent's name otherwise */
  by: string | null;
  createdAt: number;
  /** a monthly one's day of the month, so the 31st comes back on the 31st after a short month */
  dom?: number;
}

/** Which sources raise notices, quiet hours, and whether the browser's own notifications are on. */
export interface NotifySettings {
  sources: Record<Exclude<NoticeKind, "reminder">, boolean>;
  quiet: { on: boolean; from: string; to: string };
  browser: boolean;
  /** events in the user's Google calendars too, which Google itself reminds them of (Calendar step 11): off unless they say */
  google: boolean;
}

export const NOTIFY_DEFAULTS: NotifySettings = {
  sources: { post: true, followup: true, countdown: true, threshold: true, agent: true, question: true, event: true },
  quiet: { on: false, from: "22:00", to: "08:00" },
  browser: true,
  google: false,
};

/** How long before an event with a time its reminder comes. */
export const EVENT_LEAD = 10 * 60_000;

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function cleanNotify(raw: unknown): NotifySettings {
  const r = (raw && typeof raw === "object" ? raw : {}) as Partial<NotifySettings>;
  const sources = { ...NOTIFY_DEFAULTS.sources };
  for (const k of Object.keys(sources) as (keyof typeof sources)[]) if (typeof r.sources?.[k] === "boolean") sources[k] = r.sources[k];
  const q = r.quiet ?? NOTIFY_DEFAULTS.quiet;
  return {
    sources,
    quiet: { on: q.on === true, from: HHMM.test(String(q.from)) ? q.from : "22:00", to: HHMM.test(String(q.to)) ? q.to : "08:00" },
    browser: r.browser !== false,
    google: r.google === true,
  };
}

/** The moment a day and a minute of it are on a time zone's clock. */
export function atLocal(day: string, min: number, tz?: string | null): number {
  const [y, m, d] = day.split("-").map(Number);
  const want = Date.UTC(y, m - 1, d) + min * 60_000;
  let t = want;
  // twice, so a change of the clocks between the guess and the answer is met
  for (let i = 0; i < 2; i++) {
    const w = wallClock(t, tz);
    const [wy, wm, wd] = w.day.split("-").map(Number);
    const seen = Date.UTC(wy, wm - 1, wd) + w.min * 60_000;
    t += want - seen;
  }
  return t;
}

/** When a reminder that repeats comes next after it went off at `at`; a monthly one on its own day, `dom`, where the month has it. */
export function nextRepeat(at: number, repeat: Repeat, tz?: string | null, dom?: number): number {
  const w = wallClock(at, tz);
  let day = addDays(w.day, 1);
  if (repeat === "weekdays") while ([0, 6].includes(weekdayOf(day))) day = addDays(day, 1);
  if (repeat === "weekly") day = addDays(w.day, 7);
  if (repeat === "monthly") {
    const [y, m, d] = w.day.split("-").map(Number);
    const next = new Date(Date.UTC(y, m, 1));
    const last = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0)).getUTCDate();
    day = `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}-${String(Math.min(dom && dom >= 1 && dom <= 31 ? dom : d, last)).padStart(2, "0")}`;
  }
  return atLocal(day, w.min, tz);
}

/** Whether a moment falls in the user's quiet hours, on their own clock. */
export function inQuiet(t: number, s: NotifySettings, tz?: string | null): boolean {
  if (!s.quiet.on) return false;
  const min = wallClock(t, tz).min;
  const m = (x: string) => Number(x.slice(0, 2)) * 60 + Number(x.slice(3));
  const a = m(s.quiet.from);
  const b = m(s.quiet.to);
  return a <= b ? min >= a && min < b : min >= a || min < b;
}

/** Something on the board that comes due, at its moment. */
export interface Due {
  at: number;
  kind: Exclude<NoticeKind, "reminder" | "threshold" | "agent" | "question">;
  key: string;
  title: string;
  body: string | null;
  to: ActivityTo;
  board: ID;
  done?: Notice["done"];
  join?: string;
}

/** Nine in the morning on the user's clock: when a day's post or follow-up with no time of its own is due. */
const MORNING = 9 * 60;

const words = (s: unknown, n = 80) => {
  const t = String(s ?? "").replace(/\s+/g, " ").trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

/**
 * Everything on the board that comes due, at its moment: posts, follow-ups, countdowns, and ten minutes before each
 * timed event on a Calendar board (a one-off one wherever it is, each time of a repeating one from yesterday to nine
 * days on), with its call's Join;
 * google is the coming events of the Google calendars the user's Calendar boards show (each with the board that shows
 * it), for a board event's Join from its copy; with googleToo, each of them is reminded of as well, but for an
 * invitation the user said no to and a board event's own copy there.
 */
export function dueOn(board: { entries: Entry[]; items: Item[]; kinds: Map<ID, string>; google?: { ev: GcalEvent; board: ID }[]; googleToo?: boolean }, tz?: string | null, now = Date.now()): Due[] {
  const zone = cleanZone(tz);
  const out: Due[] = [];
  const from = addDays(wallClock(now, zone).day, -1);
  const to = addDays(from, 10);
  // a board event's copies in Google hold its call (step 5): Join comes from the first that has one
  const copyJoin = new Map((board.google ?? []).filter((g) => g.ev.join).map((g) => [`${g.ev.cal}#${g.ev.id}`, g.ev.join!.url]));
  const linked = new Set<string>();
  // its place, unless that is its call's link, which Join opens; no clock time, as the board writes none in the user's own way here
  // made or moved inside its ten minutes, it says how many are left when it is raised
  const soon = (title: string, where: string | undefined, start: number) => {
    const left = Math.round((start - now) / 60_000);
    const when = left > 0 && left < EVENT_LEAD / 60_000 ? `in ${left} minute${left === 1 ? "" : "s"}` : "in 10 minutes";
    return { title: `${words(title, 70) || "An event"} ${when}`, body: where && !joinIn(where) ? words(where, 80) : null };
  };
  const minOf = (at: unknown) => (typeof at === "string" && HHMM.test(at) ? Number(at.slice(0, 2)) * 60 + Number(at.slice(3)) : null);
  for (const e of board.entries) {
    const d = e.data as Record<string, unknown>;
    const kind = board.kinds.get(e.boardId);
    if (e.t === "post" && kind === "calendar" && typeof d.day === "string" && d.status !== "posted") {
      const min = minOf(d.time);
      const at = atLocal(d.day, min ?? MORNING, zone);
      const label = words(d.title) || words(Array.isArray(d.parts) ? d.parts[0] : "", 60) || "A post";
      out.push({ at, kind: "post", key: `post:${e.id}:${at}`, title: `Time to post: ${label}`, body: d.channel ? `On ${String(d.channel)}.` : null, to: { board: e.boardId, entry: e.id }, board: e.boardId });
    } else if (e.t === "event" && kind === "agenda") {
      const ev = eventOf(e);
      for (const [cal, gid] of Object.entries(ev.g ?? {})) linked.add(`${cal}#${gid}`);
      if (ev.allDay) continue;
      const join = joinIn(ev.location)?.url ?? joinIn(ev.notes)?.url ?? Object.entries(ev.g ?? {}).map(([cal, gid]) => copyJoin.get(`${cal}#${gid}`)).find(Boolean);
      // a repeating one's times in the window (the alarm looks again twice a day); a one-off one wherever it is
      for (const s of showingsOf(e, zone, from, to)) {
        if (s.allDay || dayPart(s.start) < from || (ev.repeat && dayPart(s.start) > to)) continue;
        const at = atLocal(dayPart(s.start), minPart(s.start), zone) - EVENT_LEAD;
        out.push({ at, kind: "event", key: `event:${s.id}:${at}`, ...soon(eventTitle(ev), ev.location, at + EVENT_LEAD), to: { board: e.boardId, entry: e.id }, board: e.boardId, ...(join ? { join } : {}) });
      }
    } else if (e.t === "deal" && kind === "pipeline" && typeof d.due === "string" && (d.status ?? "open") === "open") {
      const at = atLocal(d.due, MORNING, zone);
      out.push({ at, kind: "followup", key: `followup:${e.id}:${at}`, title: `Follow up: ${words(d.company) || "a client"}`, body: d.next ? words(d.next, 140) : null, to: { board: e.boardId, entry: e.id }, board: e.boardId });
    }
  }
  for (const { ev: g, board: b } of board.googleToo ? (board.google ?? []) : []) {
    if (g.allDay || g.you === "declined" || linked.has(`${g.cal}#${g.id}`)) continue;
    const at = momentOf(g.start, g.tz) - EVENT_LEAD;
    if (!Number.isFinite(at)) continue;
    out.push({ at, kind: "event", key: `event:${g.cal}#${g.id}:${at}`, ...soon(g.title, g.location, at + EVENT_LEAD), to: { board: b }, board: b, ...(g.join ? { join: g.join.url } : {}) });
  }
  for (const i of board.items) {
    if (i.kind !== "widget" || i.type !== "countdown") continue;
    const to = typeof i.props?.to === "string" ? i.props.to : "";
    const at = /^\d{4}-\d{2}-\d{2}$/.test(to) ? atLocal(to, 0, zone) : Date.parse(to);
    if (!Number.isFinite(at)) continue;
    const label = words(i.props?.label) || words(i.title) || "The countdown";
    out.push({ at, kind: "countdown", key: `countdown:${i.id}:${at}`, title: `${label}: the countdown reached zero`, body: null, to: { board: i.boardId, item: i.id }, board: i.boardId });
  }
  return out;
}

/** A number crossing the line a widget's alert sets: "above" a value, or "below" it; null when it did not cross. */
export function crossed(before: number | null, after: number | null, alert: { op: "above" | "below"; value: number } | null): boolean {
  if (!alert || after === null || !Number.isFinite(after)) return false;
  const over = (v: number) => (alert.op === "above" ? v > alert.value : v < alert.value);
  return over(after) && (before === null || !over(before));
}
